import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Activity, Heart, Stethoscope, Thermometer, Brain, Smartphone, ShieldCheck, Droplet,
    Building2, Plane, GraduationCap, Briefcase, ArrowRight, Sparkles, Video, ShoppingCart, UserPlus, Eye,
    Scale, Zap, KeyRound,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { HeroSlider } from "../components/HeroSlider";

function useCountUp(target, duration = 1600, trigger = true) {
    const [v, setV] = useState(0);
    useEffect(() => {
        if (!trigger) return;
        const start = performance.now();
        let raf;
        const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setV(Math.round(eased * target));
            if (t < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, duration, trigger]);
    return v;
}

function StatsBanner() {
    const { t } = useTranslation();
    const ref = useRef(null);
    const [seen, setSeen] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(([e]) => e.isIntersecting && setSeen(true), { threshold: 0.3 });
        io.observe(el);
        return () => io.disconnect();
    }, []);
    const a = useCountUp(247000, 1800, seen);
    const b = useCountUp(98, 1500, seen);
    const c = useCountUp(124, 1500, seen);

    return (
        <section ref={ref} className="relative bg-[#0d0d10] text-white overflow-hidden grain" data-testid="stats-banner">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(230,57,70,0.25),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(247,127,0,0.2),transparent_50%)]" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                    { value: a.toLocaleString(), label: t("home.stats.patients"), suffix: "+" },
                    { value: b, label: t("home.stats.accuracy"), suffix: "%" },
                    { value: c, label: t("home.stats.clinics"), suffix: "" },
                    { value: "30+", label: t("home.stats.vitals"), suffix: "" },
                ].map((s, i) => (
                    <div key={i} className="border-l-2 border-[var(--medx-red)] pl-5">
                        <div className="font-display text-4xl md:text-6xl font-bold tracking-tighter">
                            {s.value}
                            <span className="text-[var(--medx-orange)]">{s.suffix}</span>
                        </div>
                        <div className="text-sm text-gray-400 mt-2">{s.label}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function CapabilityBento() {
    const { t } = useTranslation();
    // 9 key vitals that mirror the kiosk's "Measure Item" screen
    const vitals = [
        { icon: Scale, key: "weightHeight", color: "from-red-500 to-orange-500" },
        { icon: Droplet, key: "spo2", color: "from-blue-500 to-cyan-500" },
        { icon: Heart, key: "bp", color: "from-rose-500 to-red-500" },
        { icon: Thermometer, key: "temperature", color: "from-amber-500 to-orange-500" },
        { icon: Activity, key: "heartRate", color: "from-red-500 to-rose-500" },
        { icon: Eye, key: "vision", color: "from-orange-500 to-amber-500" },
        { icon: Brain, key: "bodyComp", color: "from-purple-500 to-indigo-500" },
        { icon: Stethoscope, key: "bodyFat", color: "from-emerald-500 to-teal-500" },
        { icon: Zap, key: "bmi", color: "from-amber-600 to-red-500" },
    ];
    return (
        <section className="bg-gradient-to-b from-white via-orange-50/20 to-white" data-testid="capability-bento">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <div>
                        <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3">
                            {t("home.bento.eyebrow")}
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter max-w-2xl">
                            {t("home.bento.title1")}
                            <br />
                            <span className="brand-gradient-text">{t("home.bento.title2")}</span>
                        </h2>
                    </div>
                    <Link to="/solutions" className="btn-outline inline-flex items-center gap-2 self-start" data-testid="bento-explore">
                        {t("home.bento.exploreAll")} <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    {/* Kiosk image showing the measure-item screen */}
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-[var(--medx-red)]/20 to-[var(--medx-orange)]/20 blur-3xl rounded-full" />
                            <img
                                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_medx-dashboard-demo/artifacts/ep3nhemd_ChatGPT%20Image%20Sep%2019%2C%202026%2C%2011_34_13%20AM.png"
                                alt="A student using the Medx AI health & wellness kiosk — selecting a vital to measure"
                                className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[3/4]"
                                data-testid="bento-kiosk-image"
                            />
                            <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl px-5 py-3 border border-orange-100">
                                <div className="text-[10px] font-mono tracking-[0.22em] text-gray-500">INSIDE THE KIOSK</div>
                                <div className="text-sm font-semibold text-gray-900">{t("home.bento.kioskCaption")}</div>
                            </div>
                        </div>
                    </div>

                    {/* 9 key vitals grid */}
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-3 gap-3 md:gap-4">
                            {vitals.map((v, i) => {
                                const Icon = v.icon;
                                return (
                                    <div
                                        key={i}
                                        className="group aspect-square rounded-2xl bg-white border border-orange-100 p-3 md:p-4 flex flex-col items-center justify-center text-center hover:-translate-y-1 hover:shadow-lg transition-all"
                                        data-testid={`bento-vital-${v.key}`}
                                    >
                                        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br ${v.color} grid place-items-center mb-2 md:mb-3 text-white shadow-md`}>
                                            <Icon className="w-5 h-5 md:w-6 md:h-6" />
                                        </div>
                                        <div className="font-semibold text-[11px] md:text-sm text-gray-900 leading-tight">
                                            {t(`home.bento.vitals.${v.key}.title`)}
                                        </div>
                                        <div className="text-[9px] md:text-[11px] text-gray-500 mt-0.5 md:mt-1 font-mono tracking-wide">
                                            {t(`home.bento.vitals.${v.key}.unit`)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-6 text-sm text-gray-600 leading-relaxed">
                            {t("home.bento.footer")}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function HowItWorks() {
    const { t } = useTranslation();
    const steps = [
        { n: "01", key: "s1" },
        { n: "02", key: "s2" },
        { n: "03", key: "s3" },
        { n: "04", key: "s4" },
    ];
    return (
        <section className="bg-[var(--medx-cream)]" data-testid="how-it-works">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3">{t("home.howItWorks.eyebrow")}</div>
                    <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-5">
                        {t("home.howItWorks.titlePre")}<span className="brand-gradient-text">{t("home.howItWorks.titleAccent")}</span>{t("home.howItWorks.titlePost")}
                    </h2>
                    <p className="text-gray-600 leading-relaxed mb-6">
                        {t("home.howItWorks.p1")}
                    </p>
                    <Link to="/use-cases" className="btn-primary inline-flex items-center gap-2" data-testid="how-cta">
                        {t("home.howItWorks.cta")} <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="lg:col-span-7">
                    <div className="relative">
                        <img
                            src="https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/x6i6noyd_med1.png"
                            alt="Medx AI Kiosk"
                            className="rounded-3xl object-cover w-full aspect-[4/3] shadow-2xl bg-white"
                        />
                        <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 w-72">
                            <div className="text-xs font-mono text-gray-500 tracking-widest mb-1">{t("home.howItWorks.patientReading")}</div>
                            <div className="text-2xl font-display font-bold">128 / 82</div>
                            <div className="text-xs text-gray-500">{t("home.howItWorks.bpElevated")}</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                        {steps.map((s) => (
                            <div key={s.n} className="bg-white rounded-2xl border border-orange-100 p-5">
                                <div className="text-[var(--medx-orange)] font-mono text-xs font-bold mb-2">{s.n}</div>
                                <div className="font-display font-semibold text-gray-900 mb-1">{t(`home.howItWorks.steps.${s.key}.title`)}</div>
                                <div className="text-xs text-gray-500 leading-relaxed">{t(`home.howItWorks.steps.${s.key}.desc`)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function UseCasesPreview() {
    const { t } = useTranslation();
    const cases = [
        { icon: Building2, key: "hospitals", color: "from-red-50 to-orange-50" },
        { icon: Briefcase, key: "corporate", color: "from-orange-50 to-amber-50" },
        { icon: Plane, key: "airports", color: "from-amber-50 to-red-50" },
        { icon: GraduationCap, key: "schools", color: "from-red-50 to-amber-50" },
    ];
    return (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="use-cases-preview">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                <div>
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3">{t("home.useCasesPreview.eyebrow")}</div>
                    <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter max-w-3xl">
                        {t("home.useCasesPreview.title")}
                    </h2>
                </div>
                <Link to="/use-cases" className="text-[var(--medx-red)] font-semibold hover:underline">{t("home.useCasesPreview.allSectors")} →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {cases.map((c, i) => {
                    const Icon = c.icon;
                    return (
                        <div
                            key={i}
                            className={`relative rounded-2xl p-6 border border-orange-100 bg-gradient-to-br ${c.color} hover:-translate-y-1 transition-transform`}
                            data-testid={`use-case-card-${i}`}
                        >
                            <Icon className="w-8 h-8 text-[var(--medx-red)] mb-4" />
                            <div className="font-display text-lg font-semibold text-gray-900">{t(`home.useCasesPreview.cards.${c.key}`)}</div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function HowItAllWorks() {
    const { t } = useTranslation();
    return (
        <section className="bg-white" data-testid="how-it-all-works">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3">{t("home.howItAllWorks.eyebrow")}</div>
                    <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-5">
                        {t("home.howItAllWorks.titlePre")}<span className="brand-gradient-text">{t("home.howItAllWorks.titleAccent")}</span>
                    </h2>
                    <p className="text-gray-600 leading-relaxed mb-6">
                        {t("home.howItAllWorks.p1")}
                    </p>
                    <ul className="space-y-3 text-sm text-gray-700">
                        <li className="flex gap-3"><span className="text-[var(--medx-orange)] font-mono">01</span> {t("home.howItAllWorks.bullets.b1")}</li>
                        <li className="flex gap-3"><span className="text-[var(--medx-orange)] font-mono">02</span> {t("home.howItAllWorks.bullets.b2")}</li>
                        <li className="flex gap-3"><span className="text-[var(--medx-orange)] font-mono">03</span> {t("home.howItAllWorks.bullets.b3")}</li>
                        <li className="flex gap-3"><span className="text-[var(--medx-orange)] font-mono">04</span> {t("home.howItAllWorks.bullets.b4")}</li>
                    </ul>
                </div>
                <div className="lg:col-span-7">
                    <div className="relative rounded-3xl overflow-hidden bg-black aspect-video shadow-2xl border border-gray-100">
                        <video
                            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_medx-dashboard-demo/artifacts/vmehgtrm_WhatsApp%20Video%202026-10-02%20at%203.08.02%20PM.mp4"
                            controls
                            playsInline
                            poster="https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/x6i6noyd_med1.png"
                            className="w-full h-full object-cover"
                            data-testid="how-it-works-video"
                        />
                    </div>
                    <div className="text-center text-xs text-gray-400 mt-3 font-mono tracking-widest">
                        {t("home.howItAllWorks.videoCaption")}
                    </div>
                </div>
            </div>
        </section>
    );
}

function OrderCtaBanner() {
    const { t } = useTranslation();
    return (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="order-cta-banner">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-900 to-black p-10 md:p-14 text-white grain">
                <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-[var(--medx-orange)]/20 blur-3xl" />
                <div className="absolute -left-16 bottom-0 w-72 h-72 rounded-full bg-[var(--medx-red)]/20 blur-3xl" />
                <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur text-xs font-mono tracking-[0.18em] mb-5">
                            <ShoppingCart className="w-3 h-3 text-[var(--medx-orange)]" /> {t("home.orderBanner.tag")}
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter mb-4">
                            {t("home.orderBanner.titlePre")}<span className="brand-gradient-text">{t("home.orderBanner.titleAccent")}</span>
                        </h2>
                        <p className="text-gray-300 text-lg max-w-2xl mb-7 leading-relaxed">
                            {t("home.orderBanner.p1")}
                        </p>
                        <div className="flex flex-wrap items-center gap-4">
                            <Link to="/order" className="btn-secondary inline-flex items-center gap-2" data-testid="home-order-cta">
                                <ShoppingCart className="w-4 h-4" /> {t("home.orderBanner.orderNow")}
                            </Link>
                            <Link to="/contact" className="text-white/80 hover:text-[var(--medx-orange)] underline underline-offset-4 text-sm">
                                {t("common.talkToSales")} →
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-4 grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                            <div className="text-[var(--medx-orange)] font-display text-2xl font-bold">{t("home.orderBanner.buy")}</div>
                            <div className="text-white/70 text-xs mt-1">{t("home.orderBanner.buyDesc")}</div>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                            <div className="text-[var(--medx-orange)] font-display text-2xl font-bold">{t("home.orderBanner.rent")}</div>
                            <div className="text-white/70 text-xs mt-1">{t("home.orderBanner.rentDesc")}</div>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 col-span-2">
                            <div className="text-[var(--medx-orange)] font-display text-2xl font-bold">{t("home.orderBanner.earn")}</div>
                            <div className="text-white/70 text-xs mt-1">{t("home.orderBanner.earnDesc")}</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function RegisterCtaBanner() {
    const { t } = useTranslation();
    return (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12" data-testid="register-cta-banner">
            <div className="relative overflow-hidden rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-red-50 p-10 md:p-14 grain">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8">
                        <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3">{t("home.register.eyebrow")}</div>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-4">
                            {t("home.register.title")}
                        </h2>
                        <p className="text-gray-600 text-lg max-w-2xl leading-relaxed mb-6">
                            {t("home.register.p1")}
                        </p>
                        <Link to="/signup" className="btn-primary inline-flex items-center gap-2" data-testid="home-register-cta">
                            <UserPlus className="w-4 h-4" /> {t("home.register.cta")}
                        </Link>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="relative w-48 h-48 mx-auto">
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] blur-2xl opacity-40" />
                            <div className="relative w-full h-full rounded-full bg-white grid place-items-center shadow-xl border border-orange-100">
                                <div className="text-center">
                                    <div className="font-display text-6xl font-bold brand-gradient-text">100%</div>
                                    <div className="text-xs font-mono tracking-[0.18em] text-gray-500 mt-1">{t("home.register.freeForever")}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function VisionBanner() {
    const { t } = useTranslation();
    return (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12" data-testid="vision-banner">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0b0e1a] via-[#111827] to-[#1e293b] text-white p-10 md:p-14 grain">
                <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[var(--medx-orange)]/25 blur-3xl" />
                <div className="absolute -left-24 -bottom-24 w-80 h-80 rounded-full bg-[var(--medx-red)]/25 blur-3xl" />
                <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur text-xs font-mono tracking-[0.18em] mb-5">
                            <Eye className="w-3.5 h-3.5 text-[var(--medx-orange)]" /> {t("home.vision.eyebrow")}
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-4 text-white">
                            {t("home.vision.title")}
                        </h2>
                        <p className="text-white/80 text-lg max-w-2xl leading-relaxed mb-6">
                            {t("home.vision.p1")}
                        </p>
                        <ul className="space-y-2 text-sm text-white/85 mb-7">
                            <li className="flex gap-3"><span className="text-[var(--medx-orange)] font-mono">→</span> {t("home.vision.bullets.b1")}</li>
                            <li className="flex gap-3"><span className="text-[var(--medx-orange)] font-mono">→</span> {t("home.vision.bullets.b2")}</li>
                            <li className="flex gap-3"><span className="text-[var(--medx-orange)] font-mono">→</span> {t("home.vision.bullets.b3")}</li>
                            <li className="flex gap-3"><span className="text-[var(--medx-orange)] font-mono">→</span> {t("home.vision.bullets.b4")}</li>
                        </ul>
                        <Link to="/solutions#vision" className="btn-secondary inline-flex items-center gap-2" data-testid="vision-banner-cta">
                            <Eye className="w-4 h-4" /> {t("home.vision.cta")} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative rounded-2xl border border-white/10 bg-white/5 p-8 text-center backdrop-blur">
                            <div className="font-display text-7xl md:text-8xl font-bold brand-gradient-text leading-none">
                                {t("home.vision.stat")}
                            </div>
                            <div className="text-sm text-white/70 mt-4 leading-relaxed">
                                {t("home.vision.statLabel")}
                            </div>
                            <div className="mt-5 text-[10px] font-mono tracking-[0.22em] text-white/40">
                                {t("home.vision.source")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function AdvantagesBanner() {
    const { t } = useTranslation();
    const items = [
        { icon: Zap, key: "zeroWait", color: "from-red-500 to-orange-500" },
        { icon: ShieldCheck, key: "clinicalGrade", color: "from-orange-500 to-amber-500" },
        { icon: Video, key: "doctorInPocket", color: "from-rose-500 to-red-500" },
        { icon: KeyRound, key: "ownYourData", color: "from-amber-500 to-orange-600" },
    ];
    return (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="advantages-banner">
            <div className="mb-10">
                <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3">
                    {t("home.advantages.eyebrow")}
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter max-w-3xl">
                    {t("home.advantages.titlePre")}
                    <span className="brand-gradient-text">{t("home.advantages.titleAccent")}</span>
                </h2>
                <p className="text-gray-600 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
                    {t("home.advantages.intro")}
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {items.map((it, i) => {
                    const Icon = it.icon;
                    return (
                        <div
                            key={it.key}
                            className="group relative card-soft p-7 overflow-hidden hover:-translate-y-1 transition-transform"
                            data-testid={`advantage-${it.key}`}
                        >
                            <div className={`absolute -right-14 -top-14 w-44 h-44 rounded-full bg-gradient-to-br ${it.color} opacity-0 group-hover:opacity-15 blur-2xl transition-opacity`} />
                            <div className="relative">
                                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${it.color} grid place-items-center mb-5 text-white shadow-lg`}>
                                    <Icon className="w-6 h-6" />
                                </div>
                                <div className="text-[10px] font-mono tracking-[0.22em] text-[var(--medx-red)] mb-2">
                                    0{i + 1}
                                </div>
                                <div className="font-display text-xl font-semibold mb-2">
                                    {t(`home.advantages.items.${it.key}.title`)}
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {t(`home.advantages.items.${it.key}.desc`)}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function Logos() {
    const { t } = useTranslation();
    const items = [
        t("home.logos.telemedicine"),
        t("home.logos.ai"),
        t("home.logos.patientFile"),
        t("home.logos.popia"),
        t("home.logos.encrypted"),
        t("home.logos.verification"),
    ];
    return (
        <div className="border-y border-gray-100 bg-white overflow-hidden" data-testid="logos-marquee">
            <div className="flex animate-marquee py-6 whitespace-nowrap">
                {[...items, ...items, ...items].map((label, i) => (
                    <div key={i} className="px-10 font-mono text-xs tracking-[0.22em] text-gray-400">{label}</div>
                ))}
            </div>
        </div>
    );
}

/**
 * CompliancePartners — an auto-scrolling marquee of regulatory + compliance
 * badges (CE, ISO 13485, POPIA, SAHPRA). The row loops seamlessly and pauses
 * on hover so visitors can read each badge.
 */
function CompliancePartners() {
    const badges = [
        {
            name: "CE Marked",
            caption: "European conformity",
            src: "https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/5d0qoukx_ce%20logo.png",
        },
        {
            name: "ISO 13485",
            caption: "Medical device QMS",
            src: "https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/y6qodapl_ISO%20133585%20Logo.jpg",
        },
        {
            name: "POPIA Compliant",
            caption: "SA Personal Information Act",
            src: "https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/owowkp2g_popia%20onplaint.png",
        },
        {
            name: "SAHPRA",
            caption: "SA Health Products Reg. Authority",
            src: "https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/5nxbmcms_Saha%20logo.png",
        },
    ];
    // Repeat x3 so the CSS marquee loops seamlessly on all screen widths.
    const strip = [...badges, ...badges, ...badges];
    return (
        <section
            className="bg-gradient-to-b from-white to-orange-50/30 border-y border-gray-100 overflow-hidden"
            data-testid="compliance-partners"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-6 text-center">
                <div className="text-[11px] font-mono tracking-[0.22em] text-[var(--medx-red)]">TRUSTED & CERTIFIED</div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold mt-2">
                    Built to the standards that matter
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    Medx AI operates to international medical-device and data-protection standards.
                </p>
            </div>
            <div className="group relative py-6">
                <div className="flex gap-10 md:gap-16 animate-marquee whitespace-nowrap items-center group-hover:[animation-play-state:paused]">
                    {strip.map((b, i) => (
                        <div
                            key={i}
                            className="shrink-0 flex items-center gap-4 px-6 py-3 bg-white rounded-2xl border border-gray-100 shadow-sm"
                            data-testid={`compliance-badge-${b.name.replace(/\s+/g, "-").toLowerCase()}-${i}`}
                        >
                            <img
                                src={b.src}
                                alt={b.name}
                                className="h-12 md:h-14 w-auto object-contain"
                                loading="lazy"
                            />
                            <div className="text-left">
                                <div className="font-display text-sm font-semibold">{b.name}</div>
                                <div className="text-[11px] text-gray-500">{b.caption}</div>
                            </div>
                        </div>
                    ))}
                </div>
                {/* Edge fade for a polished loop */}
                <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent" />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent" />
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <div className="bg-white" data-testid="home-page">
            <Navbar />
            <HeroSlider />
            <Logos />
            <CompliancePartners />
            <CapabilityBento />
            <AdvantagesBanner />
            <HowItWorks />
            <HowItAllWorks />
            <OrderCtaBanner />
            <StatsBanner />
            <UseCasesPreview />
            <VisionBanner />
            <RegisterCtaBanner />
            <Footer />
        </div>
    );
}
